// Single-line rows for the Pinned strip's TABLE view — the vertical-economy
// counterpart of the card strip (Brendan, Jul 9: stack like the orders table,
// one line per item, never wrap; horizontal scroll absorbs the width). Built
// from the same card VMs as the strip's card view, so the two views can't
// disagree on a number.
//
// Alignment model (Brendan, Jul 9 rounds 2–3): the strip's list container is
// a GRID (see PINNED_TABLE_GRID_CLASS) and every row is a subgrid spanning
// it, so each field group is a real shared column — the widest member sets
// each leading column's width (side chit, venue chip, title, sport·date) and
// they stay vertically aligned without being forced equal-width. The stat
// cells keep fixed widths so the numeric meridians hold regardless of
// content. Pin toggle is the LEFTMOST column. Rows must emit one child per
// track (placeholders for empty slots) so auto-placement can't drift.
// The ticker is dropped from the row (it survives in the hover title).

import type { KeyboardEvent as ReactKeyboardEvent, ReactNode } from 'react';
import { PinOff, Pencil, Zap } from 'lucide-react';
import { VenueChip } from '../../components/atoms/VenueChip';
import { GameDetailLink } from '../../components/atoms/GameDetailLink';
import { SAME_GAME_HIGHLIGHT } from '../../components/cards/cardKit';
import { OrderActionButtons } from '../../components/molecules';
import { BetTeamTitle, Pill, PinToggle, SideBadge } from '../../components/cards/cardKit';
import { fmtCents, fmtQty, fmtUsd, fmtUsdSigned } from '../../components/cards/cardFormat';
import {
  fullPositionSummary,
  fullPositionTooltip,
  nowTone,
  type PositionCardVM,
} from '../../components/cards/positionCardModel';
import {
  queueTone,
  queueValuePulse,
  type OrderCardVM,
} from '../../components/cards/orderCardModel';

/** Track list shared by the container and every subgrid row: pin, side,
 *  venue, title (breathing room + absorbs slack), sport·date, detail link,
 *  then 7 stat tracks (positions use all 7; orders leave one empty) and the
 *  row actions. Lives here so index.tsx and the rows can't drift. */
export const PINNED_TABLE_GRID_CLASS =
  '[grid-template-columns:auto_auto_auto_minmax(11rem,1fr)_auto_auto_repeat(7,auto)_auto]';

/** Fixed-width stat cell: label hugs the left edge, value the right — the
 *  shared widths below are what make the numeric columns line up. */
function Cell({
  label,
  value,
  w,
  valueClass = 'text-foreground',
  title,
}: {
  label: string;
  value: ReactNode;
  /** Tailwind width class from CELL_W — same key on every row = a meridian. */
  w: string;
  valueClass?: string;
  title?: string;
}) {
  return (
    <span className={`flex ${w} items-baseline justify-between gap-1`} title={title}>
      <span className="text-muted-foreground text-[10px]">{label}</span>
      <span className={`font-mono text-[11px] font-semibold ${valueClass}`}>{value}</span>
    </span>
  );
}

/** Stat-cell widths, generous for the worst realistic value
 *  ("queue 12,345,678", "to win $9,999.99"). */
const CELL_W = {
  qty: 'w-16',
  price: 'w-28',
  queue: 'w-36',
  money: 'w-24',
  moneyWide: 'w-28',
} as const;

/** Cents + odds cell. Order rows spread a whole `PriceQuote` in, so both
 *  halves come from one price (#452); position rows pass their own pair, where
 *  the odds ride the unrounded VWAP by design (see positionCardModel avgOdds). */
function PriceValue({ cents, odds }: { cents: number | null; odds: string | null }) {
  return (
    <>
      {fmtCents(cents)}
      {odds && <span className="ml-1 text-[10px] font-normal">{odds}</span>}
    </>
  );
}

function rowShellClass(clickable: boolean): string {
  return `col-span-full grid grid-cols-subgrid items-center whitespace-nowrap px-3 py-1.5 transition-colors hover:bg-muted/20 ${
    clickable
      ? 'cursor-pointer focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-400/70'
      : ''
  }`;
}

function makeRowKeyDown(onOpen?: () => void) {
  return (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (!onOpen || e.currentTarget !== e.target) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onOpen();
    }
  };
}

/** Sport · date, minus the trailing truncated-ticker segment titleParts
 *  appends (the ticker is noise here — full ticker stays on hover). Poly
 *  subtitles carry no ' · ' and collapse to '' — fine, the side badge
 *  already says what they said. */
function subtitleMeta(subtitle: string): string {
  return subtitle.split(' · ').slice(0, -1).join(' · ');
}

/** Pin labels were snapshotted with the truncated-ticker tail ("… ·
 *  ...26JUL09INDPHX-IND") — strip it at display time so tombstones match
 *  the ticker-free rows. */
export function stripTickerTail(label: string): string {
  return label.replace(/\s·\s(\.{3}|…)\S*$/, '');
}

/** The leading columns every row shares: pin, side chit, venue chip, title
 *  (+ optional trailing pill), sport·date meta, detail link. Emits exactly
 *  six grid children — placeholders where a row has nothing — so the
 *  subgrid tracks stay honest. */
function LeadCells({
  vm,
  detailHref,
  onUnpin,
  titleExtra,
}: {
  vm: {
    title: string;
    team: string | null;
    subtitle: string;
    side: 'yes' | 'no' | null;
    venue: 'kalshi' | 'polymarket';
  };
  detailHref?: string | null;
  onUnpin: () => void;
  titleExtra?: ReactNode;
}) {
  const meta = subtitleMeta(vm.subtitle);
  return (
    <>
      <PinToggle pinned onToggle={onUnpin} />
      {vm.side ? <SideBadge side={vm.side} team={vm.team} /> : <span aria-hidden="true" />}
      <VenueChip venue={vm.venue} />
      <span className="flex min-w-0 items-center gap-1.5 pr-2">
        <span className="text-foreground truncate text-xs font-semibold leading-tight">
          <BetTeamTitle title={vm.title} team={vm.team} />
        </span>
        {titleExtra}
      </span>
      {meta ? (
        <span className="text-muted-foreground font-mono text-[10px]">{meta}</span>
      ) : (
        <span aria-hidden="true" />
      )}
      {detailHref ? (
        <GameDetailLink href={detailHref} className="-my-1" />
      ) : (
        <span aria-hidden="true" />
      )}
    </>
  );
}

export function PinnedPositionRow({
  vm,
  hidePnl,
  detailHref,
  onOpen,
  onEdit,
  onUnpin,
  highlighted = false,
  onMouseEnter,
  onMouseLeave,
}: {
  vm: PositionCardVM;
  hidePnl?: boolean;
  detailHref?: string | null;
  onOpen?: () => void;
  onEdit: () => void;
  onUnpin: () => void;
  /** Same-game highlight — true when this row's game is the hovered one. */
  highlighted?: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}) {
  const pnlClass =
    hidePnl || vm.pnlDollars == null
      ? 'text-muted-foreground'
      : vm.pnlDollars >= 0
        ? 'text-positive'
        : 'text-negative';
  const full = fullPositionSummary(vm);
  return (
    <div
      title={vm.ticker}
      role={onOpen ? 'link' : undefined}
      tabIndex={onOpen ? 0 : undefined}
      aria-label={onOpen ? `Open order book for ${vm.title}` : undefined}
      onClick={onOpen}
      onKeyDown={makeRowKeyDown(onOpen)}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={`${rowShellClass(!!onOpen)} ${highlighted ? SAME_GAME_HIGHLIGHT : ''}`}
    >
      <LeadCells
        vm={vm}
        detailHref={detailHref}
        onUnpin={onUnpin}
        titleExtra={
          // "In sale" rides the title as a pill (#173 review): the qty cell
          // is a fixed 4rem meridian with no room for a labeled figure, and
          // the order rows already park their In-sale state here — same slot,
          // same amber, so the strip reads one language.
          vm.pendingSellQty > 0 ? (
            <Pill
              className="bg-amber-400/20 text-amber-300"
              title={`${vm.pendingSellQty} contract${vm.pendingSellQty === 1 ? '' : 's'} on open sell order${vm.pendingSellQty === 1 ? '' : 's'} — still held until the sale fills`}
            >
              {fmtQty(vm.pendingSellQty)} in sale
            </Pill>
          ) : undefined
        }
      />
      <Cell
        label="qty"
        w={CELL_W.qty}
        value={
          <>
            {fmtQty(vm.qty)}
            {/* Sold-down marker (#91). The cell is 4rem, so the bought/sold
                figures the card prints inline live in the tooltip here — but
                the marker has to exist, or a residual qty looks like the whole
                position with nothing hinting otherwise. */}
            {full && (
              <span className="text-muted-foreground ml-1" title={fullPositionTooltip(full)}>
                ↓
              </span>
            )}
          </>
        }
      />
      <Cell
        label="avg"
        w={CELL_W.price}
        value={<PriceValue cents={vm.avgCents} odds={vm.avgOdds} />}
        valueClass="text-muted-foreground"
      />
      <Cell
        label="now"
        w={CELL_W.price}
        value={<PriceValue cents={vm.nowCents} odds={vm.nowOdds} />}
        valueClass={nowTone(vm.nowCents)}
      />
      <Cell label="cost" w={CELL_W.money} value={fmtUsd(vm.costDollars)} />
      <Cell
        label="P&L"
        w={CELL_W.money}
        value={vm.pnlDollars == null ? '—' : hidePnl ? '•••' : fmtUsdSigned(vm.pnlDollars)}
        valueClass={pnlClass}
      />
      <Cell label="cash" w={CELL_W.money} value={fmtUsd(vm.cashOutDollars)} />
      <Cell
        label="if win"
        w={CELL_W.moneyWide}
        value={fmtUsdSigned(vm.ifWinDollars)}
        valueClass={
          vm.ifWinDollars == null || vm.ifWinDollars >= 0 ? 'text-positive' : 'text-negative'
        }
      />
      <span className="flex items-center justify-end gap-0.5">
        {vm.editable ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
            title="Close / edit position"
            className="text-muted-foreground shrink-0 rounded p-1 transition-colors hover:bg-blue-500/10 hover:text-blue-400"
          >
            <Pencil size={12} />
          </button>
        ) : (
          <span aria-hidden="true" />
        )}
      </span>
    </div>
  );
}

export function PinnedOrderRow({
  vm,
  detailHref,
  onOpen,
  onEdit,
  onCancel,
  canceling,
  onUnpin,
  highlighted = false,
  onMouseEnter,
  onMouseLeave,
}: {
  vm: OrderCardVM;
  detailHref?: string | null;
  onOpen?: () => void;
  onEdit: () => void;
  onCancel: () => void | Promise<void>;
  canceling: boolean;
  onUnpin: () => void;
  /** Same-game highlight — true when this row's game is the hovered one. */
  highlighted?: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}) {
  return (
    <div
      title={vm.ticker}
      role={onOpen ? 'link' : undefined}
      tabIndex={onOpen ? 0 : undefined}
      aria-label={onOpen ? `Open order book for ${vm.title}` : undefined}
      onClick={onOpen}
      onKeyDown={makeRowKeyDown(onOpen)}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={`${rowShellClass(!!onOpen)} ${highlighted ? SAME_GAME_HIGHLIGHT : ''}`}
    >
      <LeadCells
        vm={vm}
        detailHref={detailHref}
        onUnpin={onUnpin}
        titleExtra={
          vm.activeInSale ? (
            <Pill
              className="bg-amber-400/20 text-amber-300"
              title="Order is partially filled and has no better level ahead"
            >
              <Zap size={9} /> In sale
            </Pill>
          ) : undefined
        }
      />
      <Cell
        label="qty"
        w={CELL_W.qty}
        value={
          vm.filling ? `${fmtQty(vm.filledQty)}/${fmtQty(vm.totalQty)}` : fmtQty(vm.totalQty)
        }
        valueClass={vm.filling ? 'text-amber-300' : 'text-foreground'}
      />
      <Cell
        label={vm.isSell ? 'ask' : 'bid'}
        w={CELL_W.price}
        value={<PriceValue {...vm.bidQuote} />}
      />
      <Cell
        label="last"
        w={CELL_W.price}
        value={<PriceValue {...vm.lastQuote} />}
        valueClass="text-muted-foreground"
      />
      <Cell
        label="queue"
        w={CELL_W.queue}
        value={vm.queue == null ? '—' : vm.queue < 1 ? 'Front' : fmtQty(vm.queue)}
        valueClass={`${queueTone(vm.queue, vm.queueTotal)} ${queueValuePulse(vm.queue)}`}
        title={
          vm.queue == null
            ? 'Queue data is temporarily unavailable; the order itself remains current'
            : undefined
        }
      />
      <Cell label="cost" w={CELL_W.money} value={fmtUsd(vm.costDollars)} />
      <Cell
        label="to win"
        w={CELL_W.moneyWide}
        value={fmtUsd(vm.toWinDollars)}
        valueClass="text-positive"
      />
      {/* Orders have six stats to positions' seven — hold the 7th track so
          mixed stacks keep their meridians. */}
      <span aria-hidden="true" />
      <span className="flex items-center justify-end gap-0.5">
        <OrderActionButtons
          canceling={canceling}
          onCancel={onCancel}
          onEdit={onEdit}
          editAvailable={vm.editAvailable}
        />
      </span>
    </div>
  );
}

/** Why a pin that closed/filled still shows: the whole point of a pin is that
 *  the trader never has to wonder where the item went. It stays, grayed, until
 *  they unpin it. Shared by the Card and Compact strips so the wording and the
 *  unpin affordance can't drift apart. */
export function PinnedTombstoneCard({
  label,
  kind,
  onUnpin,
  className = '',
}: {
  label: string;
  kind: 'position' | 'order';
  onUnpin: () => void;
  /** Width/sizing from the owning strip — Card and Compact size differently. */
  className?: string;
}) {
  return (
    <div
      className={`border-border/60 bg-card flex shrink-0 items-center gap-2 overflow-hidden rounded-lg border border-dashed px-3 py-2 ${className}`}
    >
      <span className="text-muted-foreground min-w-0 flex-1 truncate text-xs">
        {stripTickerTail(label)}
        <span className="ml-2 italic">
          — no longer open ({kind === 'position' ? 'closed or settled' : 'filled or canceled'})
        </span>
      </span>
      <button
        onClick={onUnpin}
        title="Unpin"
        aria-label="Unpin"
        className="text-muted-foreground hover:text-foreground shrink-0 rounded p-1 transition-colors"
      >
        <PinOff size={12} />
      </button>
    </div>
  );
}

/** CARD strip (#76) — one row of REAL PositionCard / OrderCard, the same
 *  components Open Positions and Open Orders render.
 *
 *  Card construction stays at the call site (renderPosition / renderOrder):
 *  the handlers, divergence maps, and edit-modal wiring all live there, and
 *  threading a dozen props through here would just move the mess. This owns
 *  what's actually strip-specific — the single-row layout, the fixed card
 *  width, and the tombstone — which is also what makes it testable without
 *  standing up the whole tracker.
 *
 *  Single row, horizontally scrolled, NOT a grid: the strip is sticky chrome
 *  parked at a viewport edge, so it must absorb width by scrolling. A grid
 *  would let it grow vertically without bound and swallow the page.
 *
 *  Equal card heights (#252) are the strip's own job, and the fill class rides
 *  the WRAPPER, not the card. `items-stretch` alone stops one level short: it
 *  stretches each wrapper to the tallest card, but the wrapper is a plain block
 *  div and the CardShell inside it is content-height, so the visible cards came
 *  out ragged-bottom. `[&>*]:h-full` carries that stretched height the last
 *  step in. It cannot live on PositionCard/OrderCard — those are shared with
 *  the Open Positions / Open Orders grids, where grid items already stretch on
 *  their own. Consequence worth keeping: CardShell is flex-col and CardBody is
 *  flex-1, so the slack lands in the body and the footers bottom-align across
 *  the strip. That alignment is the intended look, not an accident to tidy.
 *  Tombstones need nothing — they are direct flex children and already stretch. */
export function PinnedCardStrip<P, O>({
  rows,
  renderPosition,
  renderOrder,
  onUnpin,
  cardClass = 'w-[22rem]',
}: {
  /** Position and order VMs are DISTINCT type params on purpose — one shared
   *  `T` unifies to whichever the caller passes first and silently types the
   *  other branch wrong (caught by tsc the moment this had one). */
  rows: readonly {
    entry: { key: string; kind: 'position' | 'order'; label: string };
    posVM: P | null;
    orderVM: O | null;
  }[];
  renderPosition: (vm: P, entry: { key: string }) => ReactNode;
  renderOrder: (vm: O, entry: { key: string }) => ReactNode;
  onUnpin: (entry: { key: string; kind: 'position' | 'order'; label: string }) => void;
  cardClass?: string;
}) {
  return (
    <div className="flex w-max items-stretch gap-2 py-0.5">
      {rows.map(({ entry, posVM, orderVM }) => {
        if (posVM) {
          return (
            <div key={entry.key} className={`${cardClass} shrink-0 [&>*]:h-full`}>
              {renderPosition(posVM, entry)}
            </div>
          );
        }
        if (orderVM) {
          return (
            <div key={entry.key} className={`${cardClass} shrink-0 [&>*]:h-full`}>
              {renderOrder(orderVM, entry)}
            </div>
          );
        }
        return (
          <PinnedTombstoneCard
            key={entry.key}
            label={entry.label}
            kind={entry.kind}
            onUnpin={() => onUnpin(entry)}
            className={cardClass}
          />
        );
      })}
    </div>
  );
}

export function PinnedTombstoneRow({
  label,
  kind,
  onUnpin,
}: {
  label: string;
  kind: 'position' | 'order';
  onUnpin: () => void;
}) {
  return (
    <div className="col-span-full flex items-center gap-2 whitespace-nowrap px-3 py-1.5">
      <button
        onClick={onUnpin}
        title="Unpin"
        className="text-muted-foreground hover:text-foreground shrink-0 rounded p-1 transition-colors"
      >
        <PinOff size={12} />
      </button>
      <span className="text-muted-foreground text-xs">
        {stripTickerTail(label)}
        <span className="ml-2 italic">
          — no longer open ({kind === 'position' ? 'closed or settled' : 'filled or canceled'})
        </span>
      </span>
    </div>
  );
}
